<!-- DsHelzbergCard.vue -->
<template>
  <div
    class="hb-card-wrapper"
    :class="{ 'is-centered': centered }"
    ref="wrapper"
  >
    <!-- Reserve the scaled height so layout doesn’t collapse -->
    <div class="stage" :style="{ height: scaledHeight + 'px' }">
      <!-- Scale the entire card uniformly -->
      <div
        class="scale-canvas"
        ref="canvas"
        :style="{ transform: `scale(${scale})` }"
      >
        <div
          class="hb-card"
          slot="pdf-content"
          ref="pdfDownload"
          id="pdfDownload"
          :style="{
            '--card-width': width,
            '--left-panel': leftPanelWidth,
            '--surface': surfaceColor,
          }"
        >
          <!-- inner surface (light gray) -->
          <div class="hb-surface">
            <!-- top-left brand -->
            <div class="brand-left">
              <div class="dis-logo">
                <img
                  src="../assets/img/DIS-Logo-Black.png"
                  alt="logo"
                  class="logo"
                />
              </div>
              <div class="dis-label">Graded by Diamond Services USA®</div>
            </div>

            <!-- top-right headings -->
            <div class="brand-right">
              <div class="subtitle">{{ docLabel }}</div>
              <div class="title">
                <img
                  src="../assets/img/Helzberg_logo.png"
                  alt="logo"
                  class="hz-logo"
                />
              </div>
            </div>

            <!-- left white panel (photo area) -->
            <div class="left-panel">
              <div v-if="certImage" class="cert-image">
                <div style="color: red">ELECTRONIC COPY</div>
                <RemoveWhiteBG
                  :src="certImage"
                  :threshold="246"
                  :softness="14"
                  :chromaTolerance="20"
                  :maxRenderWidth="900"
                  width="70%"
                />
              </div>
              <div v-else style="color: red" class="spinner"></div>
            </div>

            <!-- lower-left notes -->
            <div class="lower-left">
              <div class="photo-note">{{ photoNote }}</div>
              <div class="site">
                <div class="url">{{ urlText }}</div>
                <div class="terms">{{ termsText }}</div>
              </div>
            </div>

            <!-- right content area -->
            <div class="right-content">
              <div class="ds-card_info-wrapper">
                <div class="cert-info">
                  <div class="cert-num-date">
                    <div class="cert-num">
                      DIS No. {{ certificate.CertNum }}
                    </div>
                    <div class="cert-date">Date: {{ CertDate }}</div>
                    <div class="cert-date">
                      SKU: {{ certificate.ClientSKU }}
                    </div>
                  </div>

                  <div class="cert-details">
                    <div v-if="certificate.ColoredStone" class="info">
                      Metal: {{ certificate.Metal }}
                    </div>
                    <div class="info">
                      Shape: {{ handleShapes(certificate) }}
                    </div>
                    <div class="info">
                      Carat: {{ handleWeight(certificate) }} ct. tw.
                    </div>
                    <div class="info">
                      Color: {{ handleColor(certificate) }}
                    </div>
                    <div class="info">
                      Clarity: {{ handleClarity(certificate) }}
                    </div>
                  </div>

                  <div class="description-wrapper">
                    <label>Description</label>
                    <div class="description">
                      {{ certificate.CardDescription }}
                    </div>
                  </div>

                  <div class="comments-wrapper">
                    <label>Comments</label>
                    <div class="comments">
                      Diamond weight estimated. Graded as mounting permits.
                    </div>
                  </div>
                </div>

                <div class="qr-code">
                  <div class="top qr-label">Scan Here</div>
                  <vue-qr
                    backgroundColor="#fff0"
                    class="qr_code"
                    :size="130"
                    :text="`https://certificates.diamondservicesusa.com/jewelry/${certificate.CertNum}`"
                  />
                  <div class="bottom qr-label">Digital Certificate</div>
                </div>
              </div>
            </div>
          </div>
          <!-- /hb-card -->
        </div>
        <!-- /scale-canvas -->
      </div>
      <!-- /stage -->
    </div>

    <div class="print-button">
      <button style="width: 300px" class="search" @click="downloadPDF">
        Download PDF
      </button>
    </div>
  </div>
</template>

<script>
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import VueQr from "vue-qr";
import RemoveWhiteBG from "@/components/RemoveWhiteBG.vue";
import { DateTime } from "luxon";

export default {
  name: "DsHelzbergCard",
  components: { RemoveWhiteBG, VueQr },
  props: {
    certificate: { type: Object, default: Object },
    certImage: { type: String, default: null },
    width: { type: String, default: "640px" }, // design width
    centered: { type: Boolean, default: true },
    leftPanelWidth: { type: String, default: "28%" },
    surfaceColor: { type: String, default: "#eceff2" },
    logoSrc: { type: String, default: "" },
    helzbergText: { type: String, default: "HELZBERG" },
    diamondsText: { type: String, default: "DIAMONDS." },
    docLabel: { type: String, default: "JEWELRY DOCUMENT" },
    photoNote: { type: String, default: "Photo Illustration" },
    urlText: { type: String, default: "www.diamondservicesusa.com" },
    termsText: {
      type: String,
      default: "See website for terms and conditions",
    },
  },
  data() {
    return {
      // scaling
      scale: 1,
      scaledHeight: 0,
      baseW: 640, // auto-detected natural width
      baseH: 400, // auto-detected natural height
      ro: null,
    };
  },
  computed: {
    CertDate() {
      return DateTime.fromMillis(this.certificate.created).toFormat(
        "MM/dd/yyyy"
      );
    },
  },
  mounted() {
    // prefer numeric width prop for first pass
    const n = parseInt(this.width, 10);
    if (!isNaN(n)) this.baseW = n;

    this.$nextTick(async () => {
      await new Promise((r) => setTimeout(r, 50));
      if (document.fonts && document.fonts.ready) {
        try {
          await document.fonts.ready;
        } catch (err) {
          console.log(err);
        }
      }

      this.detectNaturalSize();
      this.measure();

      // Recompute on container resize/rotation
      this.ro = new ResizeObserver(() => this.measure());
      this.ro.observe(this.$refs.wrapper);

      // If any image loads later, remeasure
      for (const img of this.$el.querySelectorAll("img")) {
        if (!img.complete)
          img.addEventListener(
            "load",
            () => {
              this.detectNaturalSize();
              this.measure();
            },
            { once: true }
          );
      }
    });
  },
  beforeDestroy() {
    if (this.ro) this.ro.disconnect();
  },
  unmounted() {
    if (this.ro) this.ro.disconnect();
  },
  methods: {
    // Read natural size of the card (unscaled)
    detectNaturalSize() {
      const canvas = this.$refs.canvas;
      const prev = canvas.style.transform;
      canvas.style.transform = "none";
      const el = this.$refs.pdfDownload; // the actual card root
      const naturalW = el.scrollWidth || el.offsetWidth;
      const naturalH = el.scrollHeight || el.offsetHeight;
      if (naturalW) this.baseW = naturalW;
      if (naturalH) this.baseH = naturalH;
      canvas.style.transform = prev;
    },

    // Scale to fit wrapper width; never upscale above 1
    measure() {
      const wrap = this.$refs.wrapper;
      if (!wrap) return;
      const s = Math.min(1, wrap.clientWidth / this.baseW);
      this.scale = s;
      this.scaledHeight = Math.ceil(this.baseH * s);
    },

    // (Optional) Keep if you trigger PDF from here; otherwise remove
    async downloadPDF() {
      const el = this.$refs.pdfDownload;
      if (!el) return;

      // 1) Wait for fonts/canvases/QR to finish
      await this.$nextTick();
      await new Promise((r) => setTimeout(r, 100));
      if (document.fonts && document.fonts.ready) {
        try {
          await document.fonts.ready;
        } catch (e) {
          console.log({ e });
        }
      }

      // 2) Convert any CANVAS in the live card (QR, RemoveWhiteBG) to IMG in a cloned node
      const clone = el.cloneNode(true);
      const liveCanvases = el.querySelectorAll("canvas");
      const cloneCanvases = clone.querySelectorAll("canvas");
      liveCanvases.forEach((cv, i) => {
        try {
          const dataURL = cv.toDataURL("image/png");
          const img = new Image();
          img.src = dataURL;
          img.style.width = cv.style.width || (cv.width ? `${cv.width}px` : "");
          img.style.height =
            cv.style.height || (cv.height ? `${cv.height}px` : "");
          const target = cloneCanvases[i];
          if (target && target.parentNode)
            target.parentNode.replaceChild(img, target);
        } catch (e) {
          // tainted canvas? leave it and html2canvas will rasterize it anyway
        }
      });

      // 3) Put the clone off-screen so html2canvas can snapshot it exactly as styled
      const stage = document.createElement("div");
      stage.style.cssText = `
      position: fixed; left: -99999px; top: -99999px; background: #fff;`;
      stage.appendChild(clone);
      document.body.appendChild(stage);

      // 4) Snapshot the element at high DPI
      const canvas = await html2canvas(clone, {
        backgroundColor: "#ffffff",
        scale: 3, // 2–3 gives sharp text
        useCORS: true,
        logging: false,
        removeContainer: true,
      });

      // clean up the stage
      stage.remove();

      // 5) Build a Letter-landscape PDF in PX units and center the image
      const pageW = 1056; // 11in * 96
      const pageH = 816; // 8.5in * 96
      const pdf = new jsPDF({
        unit: "px",
        format: [pageW, pageH],
        orientation: "landscape",
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.98);
      const imgW = canvas.width;
      const imgH = canvas.height;

      // Fit image inside page while preserving aspect
      const maxW = pageW / 2;
      const maxH = pageH / 2;
      const scale = Math.min(maxW / imgW, maxH / imgH, 1);
      const drawW = imgW * scale;
      const drawH = imgH * scale;

      const x = (pageW - drawW) / 2;
      const y = (pageH - drawH) / 2;

      pdf.addImage(imgData, "JPEG", x, y, drawW, drawH);
      pdf.save(`Certificate #${this.certificate.CertNum}.pdf`);
    },

    handleWeight(item) {
      if (item.ColoredStone) {
        if (item.MainStoneWeight && item.SideStoneWeight) {
          return (
            Number(item.MainStoneWeight) + Number(item.SideStoneWeight)
          ).toFixed(2);
        }
        return item.MainStoneWeight || item.SideStoneWeight || "";
      }
      if (item.MainStoneType && item.MainStoneType.includes("Diamond Accent"))
        return "Diamond Accent";
      if (item.CertType) {
        if (
          !isNaN(Number(item.MainStoneWeight)) &&
          !isNaN(Number(item.SideStoneWeight))
        ) {
          return (
            Number(item.MainStoneWeight) + Number(item.SideStoneWeight)
          ).toFixed(2);
        }
        if (!isNaN(parseInt(item.MainStoneWeight))) return item.MainStoneWeight;
        if (!isNaN(parseInt(item.SideStoneWeight))) return item.SideStoneWeight;
      } else {
        if (
          item.HasSideStones &&
          !isNaN(Number(item.MainStoneWeight)) &&
          !isNaN(Number(item.SideStoneWeight))
        ) {
          return (
            Number(item.MainStoneWeight) + Number(item.SideStoneWeight)
          ).toFixed(2);
        }
        if (!isNaN(parseInt(item.MainStoneWeight))) return item.MainStoneWeight;
        if (item.HasSideStones && !isNaN(parseInt(item.SideStoneWeight)))
          return item.SideStoneWeight;
      }
      return "";
    },
    handleShapes(item) {
      if (item.MainStoneShape && item.SideStoneShape) {
        if (item.MainStoneShape === item.SideStoneShape)
          return item.MainStoneShape;
        return `${item.MainStoneShape.replace(
          /\b(?:Brilliant|Modified)\b/gi,
          ""
        ).trim()} / ${item.SideStoneShape.replace(
          /\b(?:Brilliant|Modified)\b/gi,
          ""
        ).trim()}`;
      }
      return item.MainStoneShape || item.SideStoneShape;
    },
    handleClarity(item) {
      if (item.ColoredStone) {
        if (
          item.MainStoneType &&
          !item.MainStoneType.includes("Diamond") &&
          !item.SideStoneType.includes("Diamond")
        )
          return "";
        return `${
          item.MainStoneType && item.MainStoneType.includes("Diamond")
            ? item.MainStoneClarity2?.value
              ? `${item.MainStoneClarity.value} - ${item.MainStoneClarity2?.value} `
              : item.MainStoneClarity.value
            : "*"
        } / ${
          item.SideStoneType && item.SideStoneType.includes("Diamond")
            ? item.SideStoneClarity.value
            : "*"
        }`;
      }
      if (item.HasSideStones) {
        if (item.MainStoneClarity.value === item.SideStoneClarity.value)
          return item.MainStoneClarity.value;
        return `${item.MainStoneClarity.value} ${
          item.SideStoneClarity.value
            ? "/ " + item.SideStoneClarity.value
            : "/ *"
        }`;
      }
      if (item.MainStoneClarity.value && item.MainStoneClarity2?.value) {
        return `${item.MainStoneClarity.value} - ${item.MainStoneClarity2?.value}`;
      }
      return item.MainStoneClarity.value || item.SideStoneClarity.value;
    },
    handleColor(item) {
      const fancy = {
        FP: "Fancy Pink",
        FIP: "Fancy Intense Pink",
        FVP: "Fancy Vivid Pink",
        FB: "Fancy Black",
        FIB: "Fancy Intense Black",
        FVB: "Fancy Vivid Black",
      };
      if (item.ColoredStone) {
        let color = item.MainStoneType.includes("Diamond")
          ? item.MainStoneColor.value
          : item.MainStoneType;
        if (item.SideStoneType) {
          if (item.SideStoneType.includes("Diamond"))
            color = color + " / " + item.SideStoneColor.value;
          else if (item.SideStoneType !== item.MainStoneType)
            color = color + " / " + item.SideStoneType;
        }
        return color;
      }
      
      if (item.HasSideStones) {
        if (item.MainStoneColor.value === item.SideStoneColor.value) {
          return fancy[item.MainStoneColor.value] || item.MainStoneColor.value;
        }
        return `${
          fancy[item.MainStoneColor.value] || item.MainStoneColor.value
        } / ${fancy[item.SideStoneColor.value] || item.SideStoneColor.value}`;
      } else if (item.MainStoneColor2.value) {
        return `${
          fancy[item.MainStoneColor.value] || item.MainStoneColor.value
        } - ${fancy[item.MainStoneColor2.value] || item.MainStoneColor2.value}`;
      }
      return (
        fancy[item.MainStoneColor.value] ||
        item.MainStoneColor.value ||
        fancy[item.SideStoneColor.value] ||
        item.SideStoneColor.value
      );
    },
    handleCut(item) {
      if (item.ColoredStone) return "";
      if (item.MainStoneCut && item.SideStoneCut)
        return `${item.MainStoneCut} - ${item.SideStoneCut}`;
      return item.MainStoneCut;
    },
  },
};
</script>

<style scoped>
/* Wrapper centers the card and provides a responsive container */
.hb-card-wrapper {
  width: 100%;
  display: grid;
  justify-items: center;
  row-gap: 12px;
}
.hb-card-wrapper.is-centered {
  justify-items: center;
}

/* Reserve exact scaled height so the layout below stays put */
.stage {
  width: 100%;
}

/* The scaler applies to the whole card uniformly */
.scale-canvas {
  transform-origin: top center;
  -webkit-font-smoothing: antialiased;
  backface-visibility: hidden;
  will-change: transform;
}

/* Outside card (rounded + shadow) */
.hb-card {
  --ratio: calc(85.6 / 54);
  width: var(--card-width, 640px);
  aspect-ratio: var(--ratio);
  position: relative;
  border-radius: 16px;
  background: #212e51;
  box-shadow: 0 6px 22px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  border: 0.5px solid black;
}

/* inner surface */
.hb-surface {
  position: absolute;
  inset: 10px;
  background: #dbe1e7;
  border-radius: 12px;
  overflow: hidden;
}

/* top-right headings */
.brand-right {
  position: absolute;
  display: flex;
  align-items: baseline;
  gap: 1px;
  font-family: system-ui, -apple-system, Segoe UI, Roboto, Inter, Arial,
    sans-serif;
  width: 73%;
  justify-content: space-evenly;
  align-items: center;
  right: 0;
}
.title {
  display: inline-flex;
  align-items: baseline;
  width: 100px;
  height: 28px;
  margin-left: 8rem;
  margin-top: 0.4rem;
}
.hz-logo {
  height: 100%;
  width: 100%;
  object-fit: contain;
  filter: invert(1);
}
.subtitle {
  color: #333;
  font-weight: 600;
  letter-spacing: 0.3px;
  font-size: 14px;
}

/* top-left brand */
.brand-left {
  position: absolute;
  top: 14px;
  height: 28px;
  z-index: 99;
  display: flex;
  flex-direction: column;
  width: 100%;
  padding-left: 6px;
}
.dis-logo {
  width: 50px;
}
.logo {
  height: 100%;
  width: 100%;
  object-fit: contain;
}
.dis-label {
  font-size: 9.5px;
  font-weight: 900;
  margin-left: -1px;
}

/* left white panel */
.left-panel {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: var(--left-panel, 28%);
  background: #fff;
}

/* lower-left (inside surface, aligned with left panel) */
.lower-left {
  position: absolute;
  left: 14px;
  bottom: 16px;
  width: calc(var(--left-panel, 28%) - 28px);
  color: #2a2a2a;
  font-family: system-ui, -apple-system, Segoe UI, Roboto, Inter, Arial,
    sans-serif;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 8px;
}
.photo-note {
  opacity: 0.9;
  margin-bottom: 10px;
}
.site .url {
  font-weight: 900;
  color: #212e51;
}
.site .terms {
  font-size: 8px;
  opacity: 0.85;
  margin-top: 2px;
}

/* right content area */
.right-content {
  position: absolute;
  left: calc(var(--left-panel, 28%) + 18px);
  right: 18px;
  top: 56px;
  bottom: 18px;
  color: #111;
  font-size: 14px;
  overflow: hidden;
}

/* QR block */
.qr-code {
  position: absolute;
  right: 1rem;
  top: 0rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.qr-label {
  position: absolute;
  width: max-content;
  text-decoration: underline;
}
.top {
  top: -5px;
}
.bottom {
  bottom: -4px;
}

/* content blocks */
.cert-image {
  position: absolute;
  width: 100%;
  top: 8rem;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
}
.cert-num-date {
  margin-bottom: 5px;
}
.cert-details {
  display: block;
  height: fit-content;
}
.comments-wrapper {
  position: absolute;
  bottom: 0;
}
.description-wrapper {
  position: absolute;
  bottom: 45px;
}
.description {
  height: 90px;
  padding-right: 1rem;
}

/* Remove old width hacks; scaling handles all sizes */
</style>
